import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import DatalistInput from './DatalistInput';
import MultiTagInput from './MultiTagInput';
import {
    API_BASE,
    authHeaders,
    formatTelegramLabel,
    inputClassName,
} from '../../utils/assetHelpers';

const emptyForm = {
    community: [],
    type: 'Group',
    owner: '',
    admin: [],
    categories: [],
    groupLink: '',
    status: '',
    isPrivate: false,
    remarks: '',
};

const TelegramAssetModal = ({ mode = 'create', asset = null, meta = {}, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({ ...emptyForm });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const types = meta.telegram?.types || ['Individual', 'Group', 'Channel'];
    const statuses = meta.telegram?.statuses || meta.statuses || [];
    const communities = meta.telegram?.communities || [];
    const categories = meta.telegram?.categories || [];
    const telegramAssets = meta.telegramAssets || [];

    const ownerOptions = useMemo(() => {
        const currentId = mode === 'edit' ? asset?._id : null;
        return telegramAssets.filter((item) => String(item._id) !== String(currentId));
    }, [telegramAssets, mode, asset?._id]);

    const adminOptions = useMemo(() => {
        const currentId = mode === 'edit' ? asset?._id : null;
        return telegramAssets.filter((item) => String(item._id) !== String(currentId));
    }, [telegramAssets, mode, asset?._id]);

    useEffect(() => {
        if (mode === 'edit' && asset) {
            setFormData({
                community: asset.community || [],
                type: asset.type || 'Group',
                owner: asset.owner?._id || asset.owner || '',
                admin: (asset.admin || []).map((row) => row?._id || row).filter(Boolean),
                categories: asset.categories || [],
                groupLink: asset.groupLink || '',
                status: asset.status || '',
                isPrivate: Boolean(asset.isPrivate),
                remarks: asset.remarks || '',
            });
        }
    }, [mode, asset]);

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const toggleAdmin = (id) => {
        setFormData((prev) => {
            const exists = prev.admin.includes(id);
            return {
                ...prev,
                admin: exists ? prev.admin.filter((row) => row !== id) : [...prev.admin, id],
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                community: formData.community,
                type: formData.type,
                owner: formData.owner || null,
                admin: formData.admin,
                categories: formData.categories,
                groupLink: formData.groupLink.trim(),
                status: formData.status.trim(),
                isPrivate: formData.isPrivate,
                remarks: formData.remarks,
            };

            const request = mode === 'edit'
                ? axios.put(`${API_BASE}/api/assets/telegram/${asset._id}`, payload, { headers: authHeaders() })
                : axios.post(`${API_BASE}/api/assets/telegram`, payload, { headers: authHeaders() });

            const response = await request;
            if (response.data.success) onSuccess(response.data.data);
        } catch (err) {
            setError(`Failed to ${mode} telegram asset: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col min-h-0 shadow-lg">
                <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-gray-200 shrink-0">
                    <h2 className="text-xl font-bold">{mode === 'edit' ? 'Edit Telegram Asset' : 'Create Telegram Asset'}</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
                        {error && <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Group Link</label>
                                <input
                                    type="url"
                                    value={formData.groupLink}
                                    onChange={(e) => updateField('groupLink', e.target.value)}
                                    className={inputClassName}
                                    placeholder="https://t.me/abc"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => updateField('type', e.target.value)}
                                    className={inputClassName}
                                    required
                                >
                                    {types.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                            <DatalistInput
                                id="telegram-status-list"
                                label="Status"
                                value={formData.status}
                                onChange={(value) => updateField('status', value)}
                                options={statuses}
                            />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                                <select
                                    value={formData.owner}
                                    onChange={(e) => updateField('owner', e.target.value)}
                                    className={inputClassName}
                                >
                                    <option value="">No owner linked</option>
                                    {ownerOptions.map((item) => (
                                        <option key={item._id} value={item._id}>
                                            {formatTelegramLabel(item)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Admin</label>
                                <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                                    {adminOptions.length === 0 ? (
                                        <p className="text-sm text-gray-500">No other telegram assets available.</p>
                                    ) : (
                                        adminOptions.map((item) => (
                                            <label key={item._id} className="flex items-center gap-2 text-sm text-gray-800">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.admin.includes(item._id)}
                                                    onChange={() => toggleAdmin(item._id)}
                                                />
                                                {formatTelegramLabel(item)}
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <MultiTagInput
                                    id="telegram-community-list"
                                    label="Community"
                                    values={formData.community}
                                    onChange={(value) => updateField('community', value)}
                                    options={communities}
                                    placeholder="SGP, Spa, SBF..."
                                />
                            </div>
                            <div className="md:col-span-2">
                                <MultiTagInput
                                    id="telegram-category-list"
                                    label="Categories"
                                    values={formData.categories}
                                    onChange={(value) => updateField('categories', value)}
                                    options={categories}
                                    placeholder="Transport, Food, Forum..."
                                />
                            </div>
                            <div className="flex items-center gap-2 md:col-span-2">
                                <input
                                    id="telegram-is-private"
                                    type="checkbox"
                                    checked={formData.isPrivate}
                                    onChange={(e) => updateField('isPrivate', e.target.checked)}
                                />
                                <label htmlFor="telegram-is-private" className="text-sm font-medium text-gray-700">
                                    Private
                                </label>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                <textarea
                                    value={formData.remarks}
                                    onChange={(e) => updateField('remarks', e.target.value)}
                                    className={inputClassName}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 shrink-0">
                        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TelegramAssetModal;
